import React from 'react';
import { Input } from '@/components/Input';
import { Card, SectionHeader } from '@/components/Card';
import { FlaskConical, Wand2 } from 'lucide-react';

interface Props {
  variables: string[];                  // detected from body + header (current state)
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}

const SUGGESTIONS: Record<string, string> = {
  parameter_1: 'Sample 1',
  parameter_2: 'Sample 2',
  parameter_3: 'Sample 3',
  firstName: 'Ali',
  lastName: 'Khan',
  email: 'ali@example.com',
  phone: '+923171755276',
  whatsappPhone: '+923171755276',
  company: 'Mi Coders',
  jobTitle: 'CTO',
  city: 'Islamabad',
  country: 'Pakistan',
  orderId: '#A8472',
  amount: '$129.00',
  code: '184302',
  otp: '184302',
};

export const PreviewSamplesEditor: React.FC<Props> = ({ variables, values, onChange }) => {
  // Only show keys that currently exist as variables — never stale ones.
  const set = (k: string, v: string) => onChange({ ...values, [k]: v });

  const fillSamples = () => {
    const next = { ...values };
    variables.forEach((k) => {
      if (!next[k]) next[k] = SUGGESTIONS[k] ?? `Example ${k}`;
    });
    onChange(next);
  };

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <SectionHeader
          title="Preview sample values"
          subtitle="Used in the live preview and submitted to Meta as example data for review."
          icon={<FlaskConical size={15} />}
          className="mb-0"
        />
        {variables.length > 0 && (
          <button
            type="button"
            onClick={fillSamples}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-text-secondary transition hover:border-primary hover:text-primary"
          >
            <Wand2 size={13} /> Auto-fill
          </button>
        )}
      </div>
      <div className="px-5 py-4">
        {variables.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface-alt/50 px-4 py-5 text-center">
            <div className="text-sm font-medium text-text-secondary">No parameters yet</div>
            <div className="mt-0.5 text-xs text-text-muted">Add parameters in the body or header — sample values will appear here.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {variables.map((k) => (
              <div key={k} className="grid grid-cols-12 items-center gap-2.5">
                <span className="col-span-4 sm:col-span-3 truncate rounded-md bg-primary-soft px-2 py-1.5 font-mono text-xs font-semibold text-primary">
                  {`{{${k}}}`}
                </span>
                <Input
                  className="col-span-8 sm:col-span-9"
                  value={values[k] ?? ''}
                  onChange={(e) => set(k, e.target.value)}
                  placeholder={SUGGESTIONS[k] ?? `Sample for ${k}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
